import { useMemo } from 'react';
import { useDispatch, useStore } from 'react-redux';
import type { ActionContext } from './types';

/**
 * Build the side-effect surface that action `execute()` functions
 * run against. Returns a memoized `ActionContext` that's stable
 * across renders so passing it into a `useCallback`'s dep array
 * doesn't re-create the executor on every keystroke.
 *
 * `getState` reads through the redux store at call time rather than
 * snapshotting the state up front — same approach `useCommandContext`
 * (slash-system equivalent) uses.
 */
export default function useActionContext(): ActionContext {
  const dispatch = useDispatch();
  const store = useStore();

  return useMemo<ActionContext>(
    () => ({
      dispatch,
      getState: () => store.getState(),
    }),
    [dispatch, store],
  );
}
